import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { 
  Users, 
  Plus, 
  Search, 
  UserPlus, 
  Settings,
  Edit,
  Trash2,
  Check
} from 'lucide-react';
import { 
  useAdminGroups, 
  useCreateGroup, 
  useUpdateGroup,
  useDeleteGroup,
  useAddUsersToGroup,
  useAdminUsers
} from '../../hooks';
import { Card, Button, Badge, LoadingSpinner, Modal, Alert } from '../../components/common';
import { UserGroup, User } from '../../types';
import toast from 'react-hot-toast';

import QuickActions from '@/components/admin/QuickActions';

interface GroupFormData {
  name: string;
  description?: string;
}

interface GroupWithCount extends UserGroup {
  memberCount?: number;
}

const AdminGroups: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupWithCount | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const { data: groupsData, isLoading, refetch } = useAdminGroups();
  const { data: usersData } = useAdminUsers({ limit: 100 });
  const createGroupMutation = useCreateGroup();
  const updateGroupMutation = useUpdateGroup();
  const deleteGroupMutation = useDeleteGroup();
  const addUsersToGroupMutation = useAddUsersToGroup();

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: errorsCreate },
    reset: resetCreate
  } = useForm<GroupFormData>();

  const {
    register: registerUpdate,
    handleSubmit: handleSubmitUpdate,
    formState: { errors: errorsUpdate },
    reset: resetUpdate,
    setValue: setValueUpdate
  } = useForm<GroupFormData>();

  // CORRECCIÓN: Extraer grupos según la estructura que retorna el backend
  // El backend puede retornar { groups: [], pagination: {} } o { data: [], pagination: {} }
  const groupsArray = Array.isArray(groupsData) 
    ? groupsData 
    : (groupsData as any)?.groups || (groupsData as any)?.data || [];
  
  const groups = groupsArray as GroupWithCount[];

  // CORRECCIÓN: Extraer usuarios con type safety
  const usersArray = Array.isArray(usersData)
    ? usersData
    : (usersData as any)?.users || (usersData as any)?.data || [];
  
  const allUsers = usersArray as User[];

  // Calcular estadísticas
  const totalGroups = groups.length;
  const activeGroups = groups.filter((g: GroupWithCount) => g.status === 'active').length;
  const totalMembersInGroups = groups.reduce((sum: number, g: GroupWithCount) => 
    sum + (g.memberCount || 0), 0
  );
  const usersWithoutGroup = allUsers.length - totalMembersInGroups;

  const onCreateGroup = async (data: GroupFormData) => {
    createGroupMutation.mutate(data, {
      onSuccess: () => {
        setShowCreateModal(false);
        resetCreate();
        refetch();
      }
    });
  };

  const handleEditGroup = (group: GroupWithCount) => {
    setSelectedGroup(group);
    setValueUpdate('name', group.name);
    setValueUpdate('description', group.description || '');
    setShowEditModal(true);
  };

  const onUpdateGroup = async (data: GroupFormData) => {
    if (!selectedGroup) return;

    updateGroupMutation.mutate(
      { groupId: selectedGroup.id, data },
      {
        onSuccess: () => {
          setShowEditModal(false);
          setSelectedGroup(null);
          resetUpdate();
          refetch();
        }
      }
    );
  };

  const handleDeleteGroup = (group: GroupWithCount) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const onDeleteGroup = async () => {
    if (!selectedGroup) return;

    deleteGroupMutation.mutate(selectedGroup.id, {
      onSuccess: () => {
        setShowDeleteModal(false);
        setSelectedGroup(null);
        refetch();
      }
    });
  };

  const handleAddUsers = (group: GroupWithCount) => {
    setSelectedGroup(group);
    setSelectedUserIds([]);
    setShowAddUsersModal(true);
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const onAddUsersToGroup = async () => {
    if (!selectedGroup || selectedUserIds.length === 0) {
      toast.error('Selecciona al menos un usuario');
      return;
    }

    addUsersToGroupMutation.mutate(
      { groupId: selectedGroup.id, userIds: selectedUserIds },
      {
        onSuccess: () => {
          setShowAddUsersModal(false);
          setSelectedGroup(null);
          setSelectedUserIds([]);
          refetch();
        }
      }
    );
  };

  // CORRECCIÓN: Type annotation explícita para user
  const filteredUsers = allUsers.filter((user: User) =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.cedula.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-brinsa-azul mb-2">
              Gestión de Grupos
            </h1>
            <p className="text-lg text-brinsa-azulOscuro/80">
              Organiza usuarios en grupos para mejor administración
            </p>
          </div>

          <Button
            onClick={() => setShowCreateModal(true)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Crear Grupo
          </Button>
        </div>
      </motion.div>

      {/* Estadísticas de grupos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <Card className="p-6 text-center bg-gradient-to-b from-white to-brinsa-celeste/10 border border-brinsa-celeste/30">
          <div className="w-12 h-12 rounded-xl bg-brinsa-celeste/30 flex items-center justify-center mx-auto mb-4">
            <Users className="w-6 h-6 text-brinsa-azul" />
          </div>
          <div className="text-2xl font-bold text-brinsa-azul mb-1">{totalGroups}</div>
          <div className="text-sm text-brinsa-azulOscuro/80">Total grupos</div>
        </Card>

        <Card className="p-6 text-center bg-gradient-to-b from-white to-brinsa-celeste/10 border border-brinsa-celeste/30">
          <div className="w-12 h-12 rounded-xl bg-brinsa-celeste/30 flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 text-brinsa-azul" />
          </div>
          <div className="text-2xl font-bold text-brinsa-azul mb-1">{activeGroups}</div>
          <div className="text-sm text-brinsa-azulOscuro/80">Grupos activos</div>
        </Card>

        <Card className="p-6 text-center bg-gradient-to-b from-white to-brinsa-celeste/10 border border-brinsa-celeste/30">
          <div className="w-12 h-12 rounded-xl bg-brinsa-celeste/30 flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-6 h-6 text-brinsa-azul" />
          </div>
          <div className="text-2xl font-bold text-brinsa-azul mb-1">{totalMembersInGroups}</div>
          <div className="text-sm text-brinsa-azulOscuro/80">Usuarios asignados</div>
        </Card>

        <Card className="p-6 text-center bg-gradient-to-b from-white to-brinsa-celeste/10 border border-brinsa-celeste/30">
          <div className="w-12 h-12 rounded-xl bg-brinsa-celeste/30 flex items-center justify-center mx-auto mb-4">
            <Settings className="w-6 h-6 text-brinsa-azul" />
          </div>
          <div className="text-2xl font-bold text-brinsa-azul mb-1">{usersWithoutGroup}</div>
          <div className="text-sm text-brinsa-azulOscuro/80">Sin grupo</div>
        </Card>
      </motion.div>

      {/* Lista de grupos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <Card title="Grupos Existentes" className="border border-brinsa-celeste/30">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-brinsa-azulOscuro/50 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
                No hay grupos creados
              </h3>
              <p className="text-brinsa-azulOscuro/80 mb-4">
                Crea tu primer grupo para organizar a los usuarios
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                Crear Primer Grupo
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((group: GroupWithCount) => (
                <div
                  key={group.id}
                  className="flex items-center justify-between p-4 border rounded-lg transition-all
                             border-brinsa-celeste/30 hover:border-brinsa-azul hover:bg-brinsa-celeste/10"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-brinsa-celeste/30 flex items-center justify-center">
                      <Users className="w-5 h-5 text-brinsa-azul" />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-semibold text-brinsa-azul">{group.name}</h3>
                      <p className="text-sm text-brinsa-azulOscuro/80">{group.description || 'Sin descripción'}</p>

                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-brinsa-azulOscuro/70">
                          {group.memberCount || 0} miembros
                        </span>
                        <Badge
                          variant={group.status === 'active' ? 'success' : 'warning'}
                          size="sm"
                        >
                          {group.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddUsers(group)}
                      title="Agregar usuarios"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditGroup(group)}
                      title="Editar grupo"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteGroup(group)}
                      title="Eliminar grupo"
                      className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </motion.div>

      {/* Modal crear grupo */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Crear Nuevo Grupo"
        size="md"
      >
        <form onSubmit={handleSubmitCreate(onCreateGroup)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              Nombre del grupo *
            </label>
            <input
              {...registerCreate('name', {
                required: 'El nombre es requerido',
                minLength: { value: 2, message: 'Mínimo 2 caracteres' }
              })}
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              placeholder="Ej: Zona Norte"
            />
            {errorsCreate.name && (
              <p className="mt-1 text-sm text-red-600">{errorsCreate.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              Descripción
            </label>
            <textarea
              {...registerCreate('description')}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              placeholder="Descripción del grupo..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={createGroupMutation.isPending}
            >
              Crear Grupo
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal editar grupo */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Editar Grupo: ${selectedGroup?.name}`}
        size="md"
      >
        <form onSubmit={handleSubmitUpdate(onUpdateGroup)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              Nombre del grupo *
            </label>
            <input
              {...registerUpdate('name', {
                required: 'El nombre es requerido',
                minLength: { value: 2, message: 'Mínimo 2 caracteres' }
              })}
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
            />
            {errorsUpdate.name && (
              <p className="mt-1 text-sm text-red-600">{errorsUpdate.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              Descripción
            </label>
            <textarea
              {...registerUpdate('description')}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={updateGroupMutation.isPending}
            >
              Guardar Cambios
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal eliminar grupo */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Grupo"
        size="md"
      >
        <div className="space-y-4">
          <Alert
            type="warning"
            title="¿Estás seguro?"
            message={`Esta acción eliminará el grupo "${selectedGroup?.name}" y removerá a todos sus miembros del grupo. Esta acción no se puede deshacer.`}
          />

          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Grupo a eliminar:</strong> {selectedGroup?.name}
            </p>
            <p className="text-sm text-slate-600">
              <strong>Miembros actuales:</strong> {selectedGroup?.memberCount || 0}
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={onDeleteGroup}
              isLoading={deleteGroupMutation.isPending}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Eliminar Grupo
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal agregar usuarios */}
      <Modal
        isOpen={showAddUsersModal}
        onClose={() => setShowAddUsersModal(false)}
        title={`Agregar Usuarios a: ${selectedGroup?.name}`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-brinsa-azul/50 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar usuarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
            />
          </div>

          {/* Seleccionados */}
          {selectedUserIds.length > 0 && (
            <div className="bg-brinsa-celeste/10 p-3 rounded-lg border border-brinsa-celeste/30">
              <p className="text-sm font-medium text-brinsa-azul">
                {selectedUserIds.length} usuario(s) seleccionado(s)
              </p>
            </div>
          )}

          {/* Lista de usuarios */}
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredUsers.length === 0 ? (
              <p className="text-center text-brinsa-azulOscuro/80 py-8">
                No se encontraron usuarios
              </p>
            ) : (
              filteredUsers.map((user: User) => (
                <div
                  key={user.id}
                  onClick={() => toggleUserSelection(user.id)}
                  className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedUserIds.includes(user.id)
                      ? 'border-brinsa-azul bg-brinsa-celeste/10'
                      : 'border-slate-200 hover:border-brinsa-celeste'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-brinsa-azul rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-brinsa-azul">{user.name}</p>
                      <p className="text-xs text-brinsa-azulOscuro/70">{user.cedula}</p>
                    </div>
                  </div>

                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedUserIds.includes(user.id)
                      ? 'border-brinsa-azul bg-brinsa-azul'
                      : 'border-slate-300'
                  }`}>
                    {selectedUserIds.includes(user.id) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowAddUsersModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={onAddUsersToGroup}
              isLoading={addUsersToGroupMutation.isPending}
              disabled={selectedUserIds.length === 0}
              leftIcon={<UserPlus className="w-4 h-4" />}
            >
              Agregar {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
            </Button>
          </div>
        </div>
      </Modal>
      <QuickActions />
    </div>
  );
};

export default AdminGroups;